<template>
    <NodeViewWrapper class="image-resizable" :class="node.attrs.float ? `image-resizable--float-${node.attrs.float}` : ''">
        <div v-if="editor.isEditable" class="image-resizable__toolbar">
            <button
                class="image-resizable__toolbar-btn"
                :class="{'image-resizable__toolbar-btn--active': node.attrs.float === 'left'}"
                title="Float left"
                type="button"
                @mousedown.prevent="() => toggleFloat('left')"
            >
                <VJoyIcon name="arrow-left" size="xsmall" />
            </button>
            <button
                class="image-resizable__toolbar-btn"
                :class="{'image-resizable__toolbar-btn--active': node.attrs.float === 'right'}"
                title="Float right"
                type="button"
                @mousedown.prevent="() => toggleFloat('right')"
            >
                <VJoyIcon name="arrow-right" size="xsmall" />
            </button>
        </div>
        <img
            ref="imgRef"
            :src="node.attrs.src"
            :alt="node.attrs.alt"
            :style="node.attrs.width ? {width: `${node.attrs.width}px`} : {}"
            class="image-resizable__img"
            draggable="false"
        />
        <div v-if="editor.isEditable" class="image-resizable__handle" @mousedown.prevent="startResize" />
    </NodeViewWrapper>
</template>

<script setup lang="ts">
    import {NodeViewWrapper, nodeViewProps} from '@tiptap/vue-3';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import {ref} from 'vue';

    const props = defineProps(nodeViewProps);

    const imgRef = ref<HTMLImageElement | null>(null);

    function toggleFloat(direction: 'left' | 'right') {
        props.updateAttributes({float: props.node.attrs.float === direction ? null : direction});
    }

    function startResize(event: MouseEvent) {
        const startX = event.clientX;
        const startWidth = imgRef.value?.clientWidth ?? 300;
        const maxWidth = imgRef.value?.closest('.editor__prosemirror')?.clientWidth ?? 720;

        function onMouseMove(e: MouseEvent) {
            const newWidth = Math.min(maxWidth, Math.max(100, startWidth + (e.clientX - startX)));
            if (imgRef.value) imgRef.value.style.width = `${newWidth}px`;
        }

        function onMouseUp(e: MouseEvent) {
            const newWidth = Math.min(maxWidth, Math.max(100, startWidth + (e.clientX - startX)));
            props.updateAttributes({width: newWidth});
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        }

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }
</script>

<style lang="scss" scoped>
    .image-resizable {
        position: relative;
        display: inline-block;
        max-width: 100%;
        margin: var(--joy-core-spacing-4) 0;

        &--float-left {
            float: left;
            margin-right: var(--joy-core-spacing-6);
            margin-bottom: var(--joy-core-spacing-2);
            max-width: 50%;
        }

        &--float-right {
            float: right;
            margin-left: var(--joy-core-spacing-6);
            margin-bottom: var(--joy-core-spacing-2);
            max-width: 50%;
        }

        &__toolbar {
            position: absolute;
            top: var(--joy-core-spacing-2);
            left: var(--joy-core-spacing-2);
            display: flex;
            gap: var(--joy-core-spacing-1);
            opacity: 0;
            transition: opacity 0.15s;
        }

        &:hover &__toolbar {
            opacity: 1;
        }

        &__toolbar-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            border: none;
            border-radius: var(--joy-core-radius-2);
            background: var(--joy-color-neutral-0);
            color: var(--joy-color-text-body);
            cursor: pointer;
            box-shadow: var(--joy-core-elevation-1);

            &:hover {
                background: var(--joy-color-neutral-10);
            }

            &--active {
                background: var(--joy-color-secondary-10);
                color: var(--joy-color-secondary-50);
            }
        }

        &__img {
            display: block;
            max-width: 100%;
            border-radius: var(--joy-core-radius-3);
        }

        &__handle {
            position: absolute;
            right: -4px;
            top: 50%;
            transform: translateY(-50%);
            width: 8px;
            height: 40px;
            border-radius: var(--joy-core-radius-2);
            background: var(--joy-color-secondary-50);
            cursor: col-resize;
            opacity: 0;
            transition: opacity 0.15s;
        }

        &:hover &__handle {
            opacity: 1;
        }
    }
</style>
